<template>
  <div>
    <b-modal
      id="application-comments-modal"
      ref="application-comments-modal"
      centered
      title="Application Notes"
      no-close-on-backdrop
      @show="prepareData"
      @close="closeModal"
    >
      <b-overlay
        id="overlay-background"
        :show="isLoading"
        variant="white"
        opacity="1"
        rounded="sm"
      >
        <b-row class="mb-2 d-flex justify-content-between">
          <b-col cols="6">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search"
            />
          </b-col>
        </b-row>

        <b-card
          v-for="comment in filteredComments"
          :key="comment.application_id + comment.id"
        >
          <b-media no-body>
            <b-media-body>
              <b-card-text class="d-flex justify-content-between align-items-start">
                <div>
                  <h6
                    v-if="comment.user"
                    class="font-weight-bolder mb-0"
                  >
                    {{ comment.user.full_name }} <span class="font-small-3 text-muted">{{ comment.user.email }}</span>
                  </h6>
                  <h6
                    v-else
                    class="font-weight-bolder mb-0"
                  >
                    Unknown
                  </h6>

                  <span class="font-small-3 text-muted">
                    {{ formatDateWithTime(comment.created_at) }}
                  </span>
                  <span
                    v-if="comment.created_at !== comment.updated_at"
                    class="font-small-3 text-muted"
                  >
                    (edited)
                  </span>
                </div>

                <div
                  v-if="userData.is_super_admin || userData.id === comment.user_id"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="21"
                    class="text-primary stroke-current mr-50"
                    @click="toggleEditForm(comment)"
                  />
                  <feather-icon
                    icon="TrashIcon"
                    size="21"
                    class="text-danger stroke-current"
                    @click="deleteCommentConfirm(comment)"
                  />
                </div>
              </b-card-text>

              <b-card-text
                v-if="showEditForm && editableComment.id === comment.id"
              >
                <validation-observer
                  ref="noteRules"
                  tag="form"
                >
                  <b-form
                    class="mt-2"
                  >
                    <b-form-group>
                      <validation-provider
                        #default="{ errors }"
                        name="text"
                        rules="required"
                      >
                        <b-form-textarea
                          v-model="editableComment.data"
                          rows="4"
                          placeholder="Edit Comment"
                          class="mb-2"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>

                    <div
                      v-if="editingValidationErrors.length"
                      class="form-group pt-1"
                    >
                      <p
                        v-for="(validationError, index) in editingValidationErrors"
                        :key="index"
                        class="text-center text-danger"
                      >
                        {{ validationError }}
                      </p>
                    </div>

                    <b-card-footer
                      class="d-flex justify-content-between"
                    >
                      <b-button
                        variant="outline-secondary"
                        @click="toggleEditForm(null)"
                      >Cancel
                      </b-button>

                      <b-button
                        variant="primary"
                        @click="editComment"
                      >Update
                      </b-button>
                    </b-card-footer>
                  </b-form>
                </validation-observer>
              </b-card-text>
              <b-card-text
                v-else
                class="mb-0"
                style="white-space: pre-wrap;"
              >
                {{ comment.data }}
              </b-card-text>
            </b-media-body>
          </b-media>
        </b-card>

        <b-card
          v-if="showCreateForm"
        >
          <validation-observer
            ref="noteRules"
            tag="form"
          >
            <b-form
              class="mt-2"
            >
              <b-form-group>
                <validation-provider
                  #default="{ errors }"
                  name="text"
                  rules="required"
                >
                  <b-form-textarea
                    v-model="newComment"
                    rows="4"
                    placeholder="Add Comment"
                    class="mb-2"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <div
                v-if="creatingValidationErrors.length"
                class="form-group pt-1"
              >
                <p
                  v-for="(validationError, index) in creatingValidationErrors"
                  :key="index"
                  class="text-center text-danger"
                >
                  {{ validationError }}
                </p>
              </div>

              <b-card-footer
                class="d-flex justify-content-between"
              >
                <b-button
                  variant="outline-secondary"
                  @click="toggleCreateForm"
                >Cancel
                </b-button>

                <b-button
                  variant="primary"
                  @click="createComment"
                >Create
                </b-button>
              </b-card-footer>
            </b-form>
          </validation-observer>
        </b-card>

        <b-row
          v-else
          class="mb-2"
        >
          <b-col class="text-center">
            <b-button
              variant="primary"
              size="sm"
              @click="toggleCreateForm"
            >
              {{ 'Add Note' }}
            </b-button>
          </b-col>
        </b-row>
      </b-overlay>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="isLoading"
          @click="closeModal"
        >
          Close
        </b-button>
      </template>
    </b-modal>

    <confirmation-modal
      v-if="deletableComment"
      :type="'-comment-delete'"
      :title="'Delete Confirmation'"
      :message="'Are you sure you want to delete this comment?'"
      @close-modal="$bvModal.hide('confirmation-comment-delete')"
      @confirm="deleteComment"
    />
  </div>
</template>

<script>
import { formatDateWithTime } from '@core/utils/filter'

import {
  BModal,
  BOverlay,
  BButton,
  BCard,
  BCardText,
  BCol,
  BRow,
  BMedia,
  BMediaBody,
  BFormInput,
  BFormGroup,
  BForm,
  BFormTextarea,
  BCardFooter,
} from 'bootstrap-vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { appApplicationCommentsList } from '@/store/modules/app-application-comments'
import ConfirmationModal from '@/views/components/confirmation/ConfirmationModal.vue'
import { getUserData } from '@/auth/utils'

export default {
  components: {
    ConfirmationModal,
    ValidationProvider,
    ValidationObserver,
    BCardFooter,
    BFormTextarea,
    BForm,
    BFormGroup,
    BFormInput,
    BModal,
    BOverlay,
    BButton,
    BCard,
    BCardText,
    BCol,
    BRow,
    BMedia,
    BMediaBody,
  },
  props: {
    applicationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchQuery: '',
      showCreateForm: false,
      showEditForm: false,
      creatingValidationErrors: [],
      editingValidationErrors: [],
      userData: null,
      updatedData: false,
    }
  },
  computed: {
    filteredComments() {
      if (!this.searchQuery) return this.commentsList

      const query = this.searchQuery.toLowerCase()
      return this.commentsList.filter(comment => comment.data.toLowerCase().includes(query)
        || comment.user?.full_name.toLowerCase().includes(query)
        || comment.user?.email.toLowerCase().includes(query))
    },
    commentsList() {
      return appApplicationCommentsList.getters.getItems
    },
    isLoading() {
      return appApplicationCommentsList.getters.getIsLoading
    },
    newComment: {
      get() {
        return appApplicationCommentsList.getters.getNewComment
      },
      set(val) {
        appApplicationCommentsList.mutations.SET_NEW_COMMENT(val)
      },
    },
    editableComment: {
      get() {
        return appApplicationCommentsList.getters.getEditableComment
      },
      set(val) {
        appApplicationCommentsList.mutations.SET_EDITABLE_COMMENT(val)
      },
    },
    deletableComment: {
      get() {
        return appApplicationCommentsList.getters.getDeletableComment
      },
      set(val) {
        appApplicationCommentsList.mutations.SET_DELETABLE_COMMENT(val)
      },
    },
  },
  setup() {
    return {
      formatDateWithTime,
    }
  },
  methods: {
    async prepareData() {
      this.updatedData = false
      this.userData = getUserData()
      await this.$nextTick()

      appApplicationCommentsList.mutations.SET_APPLICATION_ID(this.applicationId)

      await this.fetchingComments()
    },
    async fetchingComments() {
      await appApplicationCommentsList.actions.fetchApplicationCommentsList()
    },
    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm
      if (!this.showCreateForm) {
        this.newComment = ''
        this.creatingValidationErrors = []
      }
    },
    toggleEditForm(comment) {
      this.showEditForm = !this.showEditForm
      if (!this.showEditForm) {
        this.editableComment = null
        this.editingValidationErrors = []
      }
      if (comment) {
        this.editableComment = JSON.parse(JSON.stringify(comment))
      } else {
        this.editableComment = {
          id: '',
          applicationId: this.applicationId,
          data: '',
        }
      }
    },
    createComment() {
      this.creatingValidationErrors = []
      return new Promise(() => {
        this.$refs.noteRules.validate().then(success => {
          if (success) {
            appApplicationCommentsList.actions.createApplicationComment()
              .then(response => {
                if (response.status === 422) {
                  const validationErrors = Object.values(response.data.errors)
                  this.creatingValidationErrors = validationErrors.flat()
                } else {
                  this.newComment = ''
                  this.showCreateForm = false
                  this.updatedData = true
                  this.fetchingComments()
                }
              })
          }
        })
      })
    },
    async editComment() {
      this.editingValidationErrors = []
      await appApplicationCommentsList.actions.updateApplicationComment()
        .then(response => {
          if (response.status === 422) {
            const validationErrors = Object.values(response.data.errors)
            this.editingValidationErrors = validationErrors.flat()
          } else {
            this.toggleEditForm()
            this.fetchingComments()
          }
        })
    },
    deleteCommentConfirm(comment) {
      this.deletableComment = comment
      this.$bvModal.show('confirmation-comment-delete')
    },
    async deleteComment() {
      await appApplicationCommentsList.actions.deleteApplicationComment()
      this.$bvModal.hide('confirmation-comment-delete')
      await this.fetchingComments()
      this.updatedData = true
    },
    async closeModal() {
      this.$bvModal.hide('application-comments-modal')
      this.$emit('close', this.updatedData)
    },
  },
}
</script>
